import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { completeSignInWithEmailLink } from '../features/auth/authService.js';
import MetaTags from '../meta/MetaTags.jsx';

/**
 * AuthCallback - Handles Firebase email-link sign-in completion
 * This page is the redirect target after a user clicks the magic link in their email
 */
function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleSignIn = async () => {
      try {
        // Get the full URL including query parameters
        const url = window.location.href;
        
        // Complete the sign-in process
        const user = await completeSignInWithEmailLink(url);
        
        if (user) {
          setStatus('success');
          
          // Redirect to account page or intended destination after 2 seconds
          setTimeout(() => {
            const redirect = searchParams.get('redirect') || '/account';
            navigate(redirect, { replace: true });
          }, 2000);
        } else {
          throw new Error('Sign-in failed: No user returned');
        }
      } catch (error) {
        console.error('[AuthCallback] Sign-in error:', error);
        setStatus('error');
        setErrorMessage(error.message || 'Failed to complete sign-in');
      }
    };

    handleSignIn();
  }, [searchParams, navigate]);

  return (
    <>
      <MetaTags profileKey="home" />
      
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        background: 'var(--color-bg-primary)',
      }}>
        <div style={{
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center',
          padding: '3rem',
          background: 'var(--color-bg-secondary)',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}>
          {status === 'processing' && (
            <>
              <div style={{
                fontSize: '3rem',
                marginBottom: '1.5rem',
                animation: 'spin 2s linear infinite',
              }}>
                🔐
              </div>
              <h1 style={{
                fontSize: '1.75rem',
                marginBottom: '1rem',
                color: 'var(--color-text-primary)',
              }}>
                Completing Sign-In
              </h1>
              <p style={{
                fontSize: '1rem',
                color: 'var(--color-text-secondary)',
              }}>
                Please wait while we verify your email link...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{
                fontSize: '3rem',
                marginBottom: '1.5rem',
              }}>
                ✅
              </div>
              <h1 style={{
                fontSize: '1.75rem',
                marginBottom: '1rem',
                color: 'var(--color-text-primary)',
              }}>
                Sign-In Successful!
              </h1>
              <p style={{
                fontSize: '1rem',
                color: 'var(--color-text-secondary)',
              }}>
                Redirecting you to your account...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{
                fontSize: '3rem',
                marginBottom: '1.5rem',
              }}>
                ❌
              </div>
              <h1 style={{
                fontSize: '1.75rem',
                marginBottom: '1rem',
                color: 'var(--color-text-primary)',
              }}>
                Sign-In Failed
              </h1>
              <p style={{
                fontSize: '1rem',
                color: 'var(--color-text-secondary)',
                marginBottom: '1.5rem',
              }}>
                {errorMessage}
              </p>
              <button
                onClick={() => navigate('/', { replace: true })}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: 'white',
                  background: 'var(--color-primary)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                Return to Home
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

export default AuthCallback;
